import {nextTick} from 'vue';
import axios from 'axios';
import {store} from '../store/global-store';
import {globalActions} from '../store/global-actions';
import {formValidationMixin} from './mixins/component-mixins';
import {SignalrConnection} from '../shared/signalr-conn';
import {modal} from './modals/modals';
import {configure, defineRule, Field, Form} from 'vee-validate';
import {max, min} from '@vee-validate/rules';
import {initListingCardComponent} from './card/listing-card';
import initListingExtraDetailsComponent from './card/listing-extra-details';
import {initCurrencyPriceComponents} from './inputs/currency-price';
import initListingContactForm from './listing-contact-form';
import initFavoriteSwitchComponent from './favorite-switch';
import initListReviewComponents from './listing-review';
import initSocialsComponent from './socials/jssocials';
import initCommentsComponents from './comments';
import initPriceHistoryChartComponent from './price-history-chart';
import initStarRatingComponent from './star-rating';
import localizedValidationMessages from '../shared/validation-localization/localization';
import {initGAComponents} from './google-analytic/ga-tracking-components';
import initAdvertPreviewComponent from './advert-preview';
import initCreateOneClickInquiry from './create-one-click-inquiry';

defineRule('min', min);
defineRule('max', max);

const checkAdvertVisibilityMixin = {
    methods: {
        async isPreviewAvailable(checkAdvertVisibilityUrl) {
            let isAdvertVisible;
            try {
                const visibilityResponse = await axios.post(checkAdvertVisibilityUrl);
                isAdvertVisible = visibilityResponse.status === 200;
            } catch (err) {
                isAdvertVisible = false;
            }

            return isAdvertVisible;
        },
    },
};

function initChatComponents(app) {
    initListingCardComponent(app);
    initListingExtraDetailsComponent(app);
    initListingContactForm(app);
    initGAComponents(app);
    initCurrencyPriceComponents(app);
    initFavoriteSwitchComponent(app);
    initListReviewComponents(app);
    initSocialsComponent(app);
    initCommentsComponents(app);
    initPriceHistoryChartComponent(app);
    initStarRatingComponent(app);
    initAdvertPreviewComponent(app);
    initCreateOneClickInquiry(app);

    configure({
        generateMessage: localizedValidationMessages(),
    });

    const chatMixin = {
        components: {
            ValidationField: Field,
            VeeValidationFormContext: Form,
        },
        methods: {
            _escapeRegExp(string) {
                return string.replace(/[.*+\-?^${}()|[\]\\]/g, '\\$&');
            },

            format(message) {
                return message.replace(new RegExp('(\r\n|\r|\n)', 'gm'), '<br/>');
            },
        },
    };

    app.component('chat-message', {
        template: '#chat-message',
        props: {
            value: {},
            hostName: {},
            formatUrl: {},
            openPreview: {
                type: Function,
                required: true,
            },
        },
        mixins: [chatMixin],
        data() {
            return {
                ...this.value,
                listings: [],
                formattedMessage: this.value.message,
            };
        },
        methods: {
            async formatMessage() {
                const urlPattern = new RegExp(`((?:(http|https)?\:\/\/)?(?:www\.)?${this._escapeRegExp(this.hostName)}\/([\\w\-\.,@?^=%&amp;:/~\+#]*[\\w\-\@?^=%&amp;/~\+#])?)`, 'ig');

                this.formattedMessage = this.format(this.message);

                let matches = this.formattedMessage.match(urlPattern);
                if (matches) {
                    matches = [...new Set(matches)];
                    await matches.forEach(async (url) => {
                        const listing = await this.getMessageListing(url);

                        if (listing) {
                            this.formattedMessage = this.formattedMessage.replaceAll(url,
                                `<a href="javascript:;" id="${listing.previewUrl}">${url}</a>`);
                            this.listings.push(listing);
                        } else {
                            this.formattedMessage = this.formattedMessage.replaceAll(url, `<a href=\"${url}\">${url}</a>`);
                        }

                        if (matches.at(-1) === url) {
                            nextTick(() => {
                                const isVisibleAdvert = listing && listing.visible;
                                this._addEventListeners(isVisibleAdvert);
                            });
                        }
                    });
                }
            },

            async getMessageListing(url) {
                let listing;
                try {
                    const response = await axios.post(this.formatUrl, null, {params: {url}});
                    listing = response.data;
                } catch (result) {
                    globalActions.handleAjaxErrors(result.response);
                }

                return listing;
            },

            _addEventListeners(isVisibleAdvert) {
                const message = this.$refs['message-text'];
                const links = message.getElementsByTagName('a');
                for (const link of links) {
                    if (link.id && isVisibleAdvert) {
                        link.addEventListener('click', () => this.openPreview(link.id));
                    }
                }
            },
        },

        async created() {
            await this.formatMessage();
        },
    });

    app.component('chat', {
        extends: modal,
        mixins: [chatMixin, formValidationMixin, checkAdvertVisibilityMixin],
        template: '#chat',

        props: {
            hubUrl: {type: String},
            hubMethods: {type: Object},
            initialUnreadConversationsCount: {type: Number},
            conversationBuffer: {type: Array},
            areConversationsPreloaded: {
                type: Boolean,
                default: false,
            },
            currentUserId: {type: String},
            systemMessageType: {type: Number},
            modalMessages: {type: Object},
            reportType: {type: Number},
            forceOpen: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                conn: null,
                currentConversation: null,
                areConversationsLoaded: this.areConversationsPreloaded,
                conversations: [],
                currentMessage: null,
                isPreviewOpened: false,
                previewSrc: null,
                isConversationListOpened: false,
                onConversationOpening: async () => {
                },
                afterClose: () => {
                },
            };
        },

        created() {
            this.conn = new SignalrConnection(this.hubUrl, {
                AddMessage: (msg) => this._addMessage(msg),
                LoadConversations: (conversations) => this._loadConversations(conversations),
                LoadThread: async (conv) => {
                    await this._loadThread(conv);
                },
                AddThread: (conv) => this._loadChatList(conv),
                SetMessageIsRead: (messageId) => this._setMessageIsRead(messageId),
                CloseReportedConversation: (convId, userName) => this._closeReportedConversation(convId, userName),
                LimitReached: () => this._showLimitReachedModal(),
            });
            this.conn.start();
        },

        mounted() {
            if (this.conversationBuffer) {
                this.conversations = this.conversationBuffer;
                if (this.forceOpen) {
                    this.$nextTick(() => {
                        this.openChat();
                        this.$nextTick(() => {
                            this._tryOpenUnreadConversation();
                        });
                    });
                }
            }

            store.subscribe((mutation) => {
                if (mutation.type === 'submitReportModal' && mutation.payload.reportType === this.reportType) {
                    this._reportSend();
                }

                if (mutation.type === 'openChat') {
                    if (mutation.payload) {
                        this.afterClose = () => {
                            globalActions.openPreview(mutation.payload.listingId,
                                mutation.payload.listingType, mutation.payload.previewUrl);
                            this.afterClose = () => {
                            };
                        };
                    }
                    nextTick(() => {
                        this.openChat();
                    });
                }

                if (mutation.type === 'openConversation') {
                    nextTick(() => {
                        this.openConversation(mutation.payload);
                        this._expandChat();
                    });
                }

                if (mutation.type === 'openChatPreview') {
                    nextTick(() => {
                        this.openPreview(mutation.payload.previewUrl);
                    });
                }
            });

            if (this.initialUnreadConversationsCount > 0) {
                store.commit('setUnreadMessagesCount', this.initialUnreadConversationsCount);
            }

            window.addEventListener('resize', this._setConversationListState, false);

            window.chat = this;
        },

        watch: {
            active(value) {
                value ? this.beforeOpen() : this.beforeClose();
            },
        },

        computed: {
            hasConversations() {
                return this.conversations.length > 0;
            },

            hasOpenedConversation() {
                return this.currentConversation !== null;
            },

            reportReasons() {
                return this.hasOpenedConversation ? this.currentConversation.reportTypes : [];
            },

            messagesGroups() {
                const groups = [];
                if (this.currentConversation) {
                    this.currentConversation.messages.forEach((message) => {
                        if (groups.length === 0 || groups[groups.length - 1].isMy !== message.isMy) {
                            groups.push({isMy: message.isMy, messages: [message], lastTime: message.createdLocal});
                        } else {
                            groups[groups.length - 1].messages.push(message);
                            groups[groups.length - 1].lastTime = message.createdLocal;
                        }
                    });
                }

                return groups;
            },

            shouldLoadConversation() {
                return !this._isMobile() && this.conversations.length > 0;
            },

            validationModel() {
                return this.currentMessage || '';
            },

            previewState() {
                return {
                    isCompactActionsMode: false,
                    currentPage: location.pathname + location.search + location.hash,
                    switchCompactActionsMode(val) {
                        this.isCompactActionsMode = val;
                    },
                    shouldReplaceHistory: false,
                };
            },
        },

        methods: {
            openChat() {
                this._expandChat();
                this._loadChatList();
            },

            closeChat() {
                this.active = false;
                this.isConversationListOpened = false;
                this.currentMessage = null;
            },

            beforeOpen() {
                store.state.shared.chatOpened = true;

                store.commit('incrementEngagesCount');
                store.commit('incrementModalsCount');

                this.activeId = store.state.shared.modalsCount;
            },
            beforeClose() {
                store.state.shared.chatOpened = false;

                if (store.state.shared.modalsCount > 0) {
                    store.commit('decrementModalsCount');
                }

                if (store.state.shared.engagesCount > 0) {
                    store.commit('decrementEngagesCount');
                }

                this.activeId = null;
                this.afterClose();
            },

            clickOutside() {
                if (this.isPreviewOpened) {
                    this.closePreview();
                } else {
                    this.closeChat();
                }
            },

            closeCurrentConversation() {
                this.currentConversation = null;
                this.isConversationListOpened = true;
                this.currentMessage = null;
            },

            async openPreview(previewSrc, forceCheckPreviewAvailable) {
                const isAdvertVisible = await this.isPreviewAvailable(previewSrc);

                if (!forceCheckPreviewAvailable || isAdvertVisible) {
                    this.previewSrc = previewSrc;
                    this.isPreviewOpened = true;
                    this.isConversationListOpened = false;
                }
            },

            closePreview() {
                this.isPreviewOpened = false;

                if (!this._isMobile()) {
                    this.isConversationListOpened = true;
                } else {
                    globalActions.showFeedbackButton();
                }
            },

            openConversation(id) {
                const conversation = this.conversations.filter((c) => c.id === id)[0];
                if (!conversation || conversation.messages.length === 0) {
                    this.conn.invoke(this.hubMethods.initiateConversationLoad, id, false);
                } else {
                    this._openConversation(conversation);
                }
            },

            isConversationOpened(conversationId) {
                return this.hasOpenedConversation && conversationId === this.currentConversation.id;
            },

            async initiateConversation(targetId, targetType) {
                const conversation = this.conversations.filter((c) => c.targetId === targetId && c.type === targetType)[0];
                if (!conversation) {
                    this.conn.invoke(this.hubMethods.tryStartConversation, targetId, targetType);
                } else {
                    await this.onConversationOpening(conversation.id);
                }
            },

            async sendMessage(conversationId, checkMessageLimit = true, shouldValidate = true, infoModalId = null) {
                if (shouldValidate) {
                    const validator = this.$refs.validator;
                    const result = await validator.validate();
                    if (!result.valid) {
                        return;
                    }
                }

                await this._sendMessageAsync(conversationId, checkMessageLimit, infoModalId);
            },

            async sendDataAsync(conversationId, checkMessageLimit = true, infoModalId = null, data = null) {
                await this._sendMessageAsync(conversationId, checkMessageLimit, infoModalId, data);
            },

            markRead(convId) {
                let conv;
                if (!convId) {
                    conv = this.currentConversation;
                } else {
                    conv = this.conversations.filter((c) => c.id === convId)[0];
                }

                const unread = this._getUnreadMessages(conv);

                if (unread.length > 0) {
                    unread.map((m) => this._markMessageRead(conv, m));
                }
            },

            scrollChat() {
                nextTick(() => {
                    if (this.$refs.messageList) {
                        this.$refs.messageList.scrollIntoView({block: 'end'});
                    }
                });
            },

            openReportModal(reportType) {
                globalActions.openReportModal({targetId: this.currentConversation.id, reportType});
            },

            formatMessage(message) {
                return this.format(message);
            },

            async _loadThread(conv) {
                if (conv) {
                    this._pushConvTopPosition(conv);
                    await this.onConversationOpening(conv.id);
                    this._updateUnreadMessagesCount();
                } else {
                    globalActions.openInfoModal(this.modalMessages.closedConversationHeader,
                        `${this.modalMessages.closedConversationReportedUserMessage} ${this.modalMessages.closedConversationMessageEnd}`);
                }
            },

            async _sendMessageAsync(conversationId, checkMessageLimit = true, infoModalId = null, data = null) {
                if (this.currentMessage) {
                    const trimmedMessage = this._trimMessage(this.currentMessage);
                    if (trimmedMessage.length > 0) {
                        if (data) {
                            data.conversationId = conversationId;
                            data.messageText = trimmedMessage;
                            data.checkMessageLimit = checkMessageLimit;

                            this.conn.invoke(this.hubMethods.sendData, data);
                        } else {
                            this.conn.invoke(this.hubMethods.sendMessage, conversationId, trimmedMessage, checkMessageLimit);
                        }

                        this.currentMessage = '';
                        if (!this.active) {
                            globalActions.openInfoModal(
                                this.modalMessages.messageSentTitle,
                                this.modalMessages.messageSentDescription,
                                null,
                                null,
                                infoModalId,
                            );
                        }
                    }
                }
            },

            _tryOpenUnreadConversation() {
                if (this.conversations.length > 0) {
                    const unreadConversations = this.conversations.filter((conversation) => conversation.newMessages > 0);
                    if (unreadConversations && unreadConversations.length > 0) {
                        this._openConversation(unreadConversations[0]);
                    } else {
                        this._openConversation(this.conversations[0]);
                    }
                }
            },

            _expandChat() {
                this.active = true;
                this.isConversationListOpened = true;
            },

            _addMessage(msg) {
                const conv = this.conversations.filter((c) => c.id === msg.conversationId)[0];

                if (!conv || msg.type === this.systemMessageType) {
                    const justToList = conv == null;
                    this.conn.invoke(this.hubMethods.initiateConversationLoad, msg.conversationId, justToList);
                } else {
                    this._pushConvTopPosition(conv);
                    conv.details = msg.message;
                    conv.messages.push(msg);
                    if (!msg.isMy) {
                        conv.newMessages++;
                        this._updateUnreadMessagesCount();
                    }
                    if (this.currentConversation != null && this.currentConversation.id === conv.id) {
                        this.scrollChat();
                    }
                }
            },

            _loadConversations(conversations) {
                this.areConversationsLoaded = true;
                this.conversations = conversations;
                if (this.shouldLoadConversation) {
                    this._openConversation(this.conversations[0]);
                }
            },

            _openConversation(conv) {
                if (conv !== this.currentConversation) {
                    this.currentMessage = null;
                }
                this.currentConversation = conv;
                this._markOpenedConversationRead();
                this._updateUnreadMessagesCount();
                if (this._isMobile()) {
                    this.isConversationListOpened = false;
                }
                this.scrollChat();
            },

            _pushConvTopPosition(conv) {
                const convPos = this.conversations.findIndex((c) => c.id === conv.id);
                if (convPos >= 0) {
                    this.conversations.splice(convPos, 1);
                }
                this.conversations.unshift(conv);
            },

            _closeReportedConversation(convId, userName) {
                this.conversations = this.conversations.filter((c) => c.id !== convId);
                if (this.currentConversation.id === convId) {
                    this.closeCurrentConversation();
                }

                globalActions.openInfoModal(this.modalMessages.closedConversationHeader,
                    `${this.modalMessages.closedConversationMessageStart} ${userName} ${this.modalMessages.closedConversationMessageEnd}`);
            },

            _reportSend() {
                this.markRead(this.currentConversation.id);
                this.conversations = this.conversations.filter((c) => c.id !== this.currentConversation.id);
                this.closeCurrentConversation();
            },

            _findMessage(messageId) {
                if (this.conversations) {
                    for (let index = 0; index < this.conversations.length; index++) {
                        const conv = this.conversations[index];

                        for (let j = 0; j < conv.messages.length; j++) {
                            if (conv.messages[j].id === messageId) {
                                return conv.messages[j];
                            }
                        }
                    }
                }

                return null;
            },

            _trimMessage(message) {
                if (!message) {
                    return message;
                }

                return message.replace(/^[\n]+|[\n]+$/g, '');
            },

            _loadChatList() {
                if (!this.areConversationsLoaded) {
                    this.conn.invoke(this.hubMethods.initiateConversationsLoad);
                } else {
                    if (this.shouldLoadConversation) {
                        const conversation = this.currentConversation !== null ? this.currentConversation : this.conversations[0];
                        this._openConversation(conversation);
                    }
                }
            },

            _getUnreadMessages(conv) {
                return conv.messages.filter((m) => m.isMy === false && m.isRead === false && m.readerId === this.currentUserId);
            },

            _markOpenedConversationRead() {
                if (this.currentConversation.newMessages > 0) {
                    this.markRead();
                }
            },

            _markMessageRead(conv, msg) {
                if (conv.newMessages > 0) {
                    conv.newMessages--;
                }

                msg.isRead = true;
                this._updateUnreadMessagesCount();
                this.conn.invoke(this.hubMethods.markRead, msg.id, msg.authorId);
            },

            _setMessageIsRead(messageId) {
                const message = this._findMessage(messageId);

                if (message) {
                    message.isRead = true;
                }
            },

            _updateUnreadMessagesCount() {
                let messagesCount = 0;
                if (this.hasConversations) {
                    messagesCount = this.conversations.reduce((sum, i) => {
                        return i.newMessages > 0 ? ++sum : sum;
                    }, 0);

                    store.commit('setUnreadMessagesCount', messagesCount);
                }
            },

            _isMobile() {
                const mobileDeviceTriggerWidth = 576;
                return window.innerWidth < mobileDeviceTriggerWidth;
            },

            _setConversationListState() {
                if (!this._isMobile()) {
                    if (!this.isPreviewOpened) {
                        this.isConversationListOpened = true;
                    }
                    if (!this.hasOpenedConversation) {
                        this._tryOpenUnreadConversation();
                    }
                } else {
                    if (this.hasOpenedConversation && this.isConversationListOpened) {
                        this.closeCurrentConversation();
                    }
                }
            },

            _showLimitReachedModal() {
                globalActions.openInfoModal(this.modalMessages.limitReachedHeader, this.modalMessages.limitReachedMessage);
            },
        },
        beforeUnmount() {
            window.removeEventListener('resize', this._setConversationListState);
        },

    });
}

export default initChatComponents;
